import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { ArrowLeft, MapPin, Wifi, Loader2, WifiOff, Lock, Clock } from 'lucide-react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { toast } from 'sonner';
import { queueOperation, isOnline } from '../../lib/pwa';

/**
 * MobileCheckIn — race-day check-in screen.
 *
 * Phase D gating rules:
 *   • Check-in is locked until the event start time (event.date).
 *   • Once the organizer presses Start (event.race_started_at set),
 *     check-in CLOSES exactly 30 minutes after that timestamp.
 */
const CHECKIN_WINDOW_AFTER_START_MS = 30 * 60 * 1000;

export default function MobileCheckIn() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: event, loading } = useApi(api.getEvent, [id]);
  const [insideZone, setInsideZone] = useState(true);
  const [accuracy, setAccuracy] = useState(8);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Tick once per minute so the lock/unlock state stays live without a refresh.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const i = setInterval(() => setAccuracy(5 + Math.random() * 6), 1500);
    return () => clearInterval(i);
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!event) return null;

  const eventTs = new Date(event.date).getTime();
  const raceStartedTs = event.raceStartedAt ? new Date(event.raceStartedAt).getTime() : null;
  const checkinClosedTs = raceStartedTs ? raceStartedTs + CHECKIN_WINDOW_AFTER_START_MS : null;

  // 1) Not yet the day/time of the event — check-in opens later.
  const tooEarly = now < eventTs;
  // 2) Race started, and we're past the 30-minute grace window.
  const closedAfterStart = checkinClosedTs != null && now > checkinClosedTs;
  const locked = tooEarly || closedAfterStart;

  async function handleConfirm() {
    if (locked) return;
    setBusy(true);
    try {
      const { queued } = await queueOperation('checkin', id, () => api.checkIn(id));
      if (queued) {
        toast.success('Offline — check-in queued, will sync when reconnected');
      }
      nav(`/events/${id}/ready`);
    } catch (err) {
      toast.error(err.message || 'Check-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-4 pt-4 pb-6 min-h-full flex flex-col" data-testid="checkin-screen">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground mb-4" data-testid="checkin-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6">
        {locked ? (
          <CheckinLocked event={event} tooEarly={tooEarly} closedAfterStart={closedAfterStart} />
        ) : (
          <>
            <div className="relative">
              <div className={`h-32 w-32 rounded-full border-2 ${insideZone ? 'border-success' : 'border-destructive'} flex items-center justify-center`}>
                <MapPin className={`h-12 w-12 ${insideZone ? 'text-success' : 'text-destructive'}`} />
              </div>
              {insideZone && <div className="absolute inset-0 rounded-full border-2 border-success animate-pulse-ring" />}
            </div>

            <div>
              <p className="rivl-label">Check-in</p>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                {insideZone ? 'You are in the start zone' : 'Outside start zone'}
              </h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
                {insideZone
                  ? 'Stay here until the race begins. We will guide you when the official start opens.'
                  : 'Move closer to the start area to enable check-in.'}
              </p>
            </div>

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {isOnline() ? (
                <><Wifi className="h-3 w-3" /> GPS accuracy ±{accuracy.toFixed(1)}m</>
              ) : (
                <><WifiOff className="h-3 w-3 text-destructive" /> Offline — will sync</>
              )}
              <button
                onClick={() => setInsideZone((v) => !v)}
                className="ml-2 text-[10px] underline opacity-50 hover:opacity-100"
                data-testid="checkin-toggle-zone"
              >
                (demo: toggle zone)
              </button>
            </div>
          </>
        )}
      </div>

      {locked ? (
        <Button asChild variant="outline" className="w-full h-12 rounded-md mt-6" data-testid="checkin-locked-back">
          <Link to={`/events/${id}`}>Back to event</Link>
        </Button>
      ) : (
        <Button
          onClick={handleConfirm}
          disabled={!insideZone || busy}
          className="w-full h-12 rounded-md mt-6"
          data-testid="checkin-confirm-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm check-in'}
        </Button>
      )}
    </div>
  );
}

function CheckinLocked({ event, tooEarly, closedAfterStart }) {
  return (
    <>
      <div className="h-32 w-32 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
        <Lock className="h-12 w-12 text-muted-foreground" />
      </div>
      <div data-testid={tooEarly ? 'checkin-too-early' : 'checkin-closed-after-start'}>
        <p className="rivl-label">Check-in</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          {tooEarly ? 'Check-in not open yet' : 'Check-in window closed'}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
          {tooEarly
            ? <>Opens on event day. <br /><span className="font-medium text-foreground">{new Date(event.date).toLocaleString()}</span></>
            : closedAfterStart
              ? 'The 30-minute check-in window after the race start has passed.'
              : null}
        </p>
        {tooEarly && <p className="mt-3 text-[11px] text-muted-foreground flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> We'll notify you when it opens.</p>}
      </div>
    </>
  );
}
